import {
  COMPUTE_BUDGET_PROGRAM_ADDRESS,
  parseSetComputeUnitLimitInstruction,
  parseSetComputeUnitPriceInstruction,
} from "@solana-program/compute-budget";
import {
  parseTransferCheckedInstruction as parseTransferCheckedInstructionToken,
  TOKEN_PROGRAM_ADDRESS,
} from "@solana-program/token";
import {
  findAssociatedTokenPda,
  parseTransferCheckedInstruction as parseTransferCheckedInstruction2022,
  TOKEN_2022_PROGRAM_ADDRESS,
} from "@solana-program/token-2022";
import {
  decompileTransactionMessage,
  getCompiledTransactionMessageDecoder,
  type Address,
} from "@solana/kit";
import type {
  PaymentPayload,
  PaymentRequirements,
  SchemeNetworkFacilitator,
  SettleResponse,
  VerifyResponse,
} from "@payai/x402/types";
import {
  LIGHTHOUSE_PROGRAM_ADDRESS,
  MAX_COMPUTE_UNIT_LIMIT,
  MAX_COMPUTE_UNIT_PRICE_MICROLAMPORTS,
  MAX_REQUIRED_SIGNATURES,
  MEMO_PROGRAM_ADDRESS,
} from "../../constants";
import { SettlementCache } from "../../settlement-cache";
import type { FacilitatorSvmSigner } from "../../signer";
import type { ExactSvmPayloadV2 } from "../../types";
import {
  decodeTransactionFromPayload,
  transactionMessageHash,
} from "../../utils";
import { normalizeTransaction, type NormalizedTransaction } from "../../normalizer";

/**
 * SVM facilitator implementation for the Exact payment scheme.
 */
export class ExactSvmScheme implements SchemeNetworkFacilitator {
  readonly scheme = "exact";
  readonly caipFamily = "solana:*";

  private readonly settlementCache: SettlementCache;

  /**
   * Creates a new ExactSvmFacilitator instance.
   *
   * @param signer - The SVM RPC client for facilitator operations
   * @param settlementCache - Optional shared settlement cache (one is created if omitted)
   * @returns ExactSvmFacilitator instance
   */
  constructor(
    private readonly signer: FacilitatorSvmSigner,
    settlementCache?: SettlementCache,
  ) {
    this.settlementCache = settlementCache ?? new SettlementCache();
  }

  /**
   * Get mechanism-specific extra data for the supported kinds endpoint.
   * For SVM, this includes a randomly selected fee payer address.
   * Random selection distributes load across multiple signers.
   *
   * @param _ - The network identifier (unused for SVM)
   * @returns Extra data with feePayer address
   */
  getExtra(_: string): Record<string, unknown> | undefined {
    // Randomly select from available signers to distribute load
    const addresses = this.signer.getAddresses();
    const randomIndex = Math.floor(Math.random() * addresses.length);

    return {
      feePayer: addresses[randomIndex],
    };
  }

  /**
   * Get signer addresses used by this facilitator.
   * For SVM, returns all available fee payer addresses.
   *
   * @param _ - The network identifier (unused for SVM)
   * @returns Array of fee payer addresses
   */
  getSigners(_: string): string[] {
    return [...this.signer.getAddresses()];
  }

  /**
   * Verifies a payment payload.
   *
   * @param payload - The payment payload to verify
   * @param requirements - The payment requirements
   * @returns Promise resolving to verification response
   */
  async verify(
    payload: PaymentPayload,
    requirements: PaymentRequirements,
  ): Promise<VerifyResponse> {
    const exactSvmPayload = payload.payload as ExactSvmPayloadV2;

    // Step 1: Validate Payment Requirements
    if (payload.accepted.scheme !== "exact" || requirements.scheme !== "exact") {
      return {
        isValid: false,
        invalidReason: "unsupported_scheme",
        payer: "",
      };
    }

    if (payload.accepted.network !== requirements.network) {
      return {
        isValid: false,
        invalidReason: "network_mismatch",
        payer: "",
      };
    }

    if (!requirements.extra?.feePayer || typeof requirements.extra.feePayer !== "string") {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_missing_fee_payer",
        payer: "",
      };
    }

    // Verify that the requested feePayer is managed by this facilitator
    const signerAddresses = this.signer.getAddresses().map(addr => addr.toString());
    if (!signerAddresses.includes(requirements.extra.feePayer)) {
      return {
        isValid: false,
        invalidReason: "fee_payer_not_managed_by_facilitator",
        payer: "",
      };
    }

    // Step 2: Parse and Validate Transaction Structure
    let transaction;
    try {
      transaction = decodeTransactionFromPayload(exactSvmPayload);
    } catch {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_transaction_could_not_be_decoded",
        payer: "",
      };
    }

    const compiled = getCompiledTransactionMessageDecoder().decode(transaction.messageBytes);
    const decompiled = decompileTransactionMessage(compiled);
    let instructions = decompiled.instructions ?? [];

    // Security check: Verify signer count to prevent multi-sig spam attacks
    // Typical x402 flow requires only 2 signatures (payer + fee payer)
    //
    // NOTE:
    // Different `@solana/kit` major versions expose different header field names.
    // - newer: `numRequiredSignatures` (canonical Solana name)
    // - older: `numSignerAccounts`
    // We support both to avoid dependency/version drift in downstream wrappers.
    const header = compiled.header as unknown as {
      numRequiredSignatures?: number;
      numSignerAccounts?: number;
    };
    const numRequiredSignatures = header.numRequiredSignatures ?? header.numSignerAccounts;
    if (
      typeof numRequiredSignatures === "number" &&
      numRequiredSignatures > MAX_REQUIRED_SIGNATURES
    ) {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_excessive_signers",
        payer: "",
      };
    }

    // Normalize the transaction (handles Swig, regular, and future wallet types)
    let normalized: NormalizedTransaction;
    try {
      normalized = await normalizeTransaction(
        instructions,
        compiled.staticAccounts ?? [],
        transaction,
      );
    } catch {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_no_transfer_instruction",
        payer: "",
      };
    }
    instructions = normalized.instructions;
    const payer = normalized.payer;

    // Instruction count check AFTER flattening (3-6)
    // - 3 instructions: ComputeLimit + ComputePrice + TransferChecked
    // - 4 instructions: ComputeLimit + ComputePrice + TransferChecked + Lighthouse or Memo
    // - 5 instructions: ComputeLimit + ComputePrice + TransferChecked + Lighthouse + Lighthouse or Memo
    // - 6 instructions: ComputeLimit + ComputePrice + TransferChecked + Lighthouse + Lighthouse + Memo
    // See: https://github.com/x402-foundation/x402/issues/828
    if (instructions.length < 3 || instructions.length > 6) {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_transaction_instructions_length",
        payer: "",
      };
    }

    // Step 3: Verify Compute Budget Instructions
    try {
      this.verifyComputeLimitInstruction(instructions[0] as never);
      this.verifyComputePriceInstruction(instructions[1] as never);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return {
        isValid: false,
        invalidReason: errorMessage,
        payer: "",
      };
    }

    // Step 4: Verify Transfer Instruction (unified — works for both regular and Swig)
    const transferIx = instructions[2];
    const transferProgramAddress = transferIx.programAddress.toString();

    if (
      transferProgramAddress !== TOKEN_PROGRAM_ADDRESS.toString() &&
      transferProgramAddress !== TOKEN_2022_PROGRAM_ADDRESS.toString()
    ) {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_no_transfer_instruction",
        payer,
      };
    }

    // Parse the transfer instruction using the appropriate library helper
    let parsedTransfer;
    try {
      if (transferProgramAddress === TOKEN_PROGRAM_ADDRESS.toString()) {
        parsedTransfer = parseTransferCheckedInstructionToken(transferIx as never);
      } else {
        parsedTransfer = parseTransferCheckedInstruction2022(transferIx as never);
      }
    } catch {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_no_transfer_instruction",
        payer,
      };
    }

    // Verify that the facilitator's signers are not transferring their own funds
    // SECURITY: Prevent facilitator from signing away their own tokens
    // For Swig txs, the authority is the Swig PDA (signs via CPI)
    const authorityAddress = parsedTransfer.accounts.authority.address.toString();
    if (signerAddresses.includes(authorityAddress)) {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_transaction_fee_payer_transferring_funds",
        payer,
      };
    }

    // Verify mint address matches requirements
    const mintAddress = parsedTransfer.accounts.mint.address.toString();
    if (mintAddress !== requirements.asset) {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_mint_mismatch",
        payer,
      };
    }

    // Verify destination ATA matches expected ATA for payTo address
    const destATA = parsedTransfer.accounts.destination.address.toString();
    try {
      const [expectedDestATA] = await findAssociatedTokenPda({
        mint: requirements.asset as Address,
        owner: requirements.payTo as Address,
        tokenProgram:
          transferProgramAddress === TOKEN_PROGRAM_ADDRESS.toString()
            ? (TOKEN_PROGRAM_ADDRESS as Address)
            : (TOKEN_2022_PROGRAM_ADDRESS as Address),
      });

      if (destATA !== expectedDestATA.toString()) {
        return {
          isValid: false,
          invalidReason: "invalid_exact_svm_payload_recipient_mismatch",
          payer,
        };
      }
    } catch {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_recipient_mismatch",
        payer,
      };
    }

    // Verify transfer amount meets requirements
    const amount = parsedTransfer.data.amount;
    if (amount !== BigInt(requirements.amount)) {
      return {
        isValid: false,
        invalidReason: "invalid_exact_svm_payload_amount_mismatch",
        payer,
      };
    }

    // Step 5: Verify optional instructions (if present)
    // Allowed optional programs: Lighthouse (wallet protection) and Memo (uniqueness)
    const optionalInstructions = instructions.slice(3);
    const invalidReasonByIndex = [
      "invalid_exact_svm_payload_unknown_fourth_instruction",
      "invalid_exact_svm_payload_unknown_fifth_instruction",
      "invalid_exact_svm_payload_unknown_sixth_instruction",
    ];

    for (let i = 0; i < optionalInstructions.length; i += 1) {
      const programAddress = optionalInstructions[i].programAddress.toString();
      if (
        programAddress === LIGHTHOUSE_PROGRAM_ADDRESS ||
        programAddress === MEMO_PROGRAM_ADDRESS
      ) {
        continue;
      }

      return {
        isValid: false,
        invalidReason:
          invalidReasonByIndex[i] ?? "invalid_exact_svm_payload_unknown_optional_instruction",
        payer,
      };
    }

    // Step 5b: Verify memo content matches extra.memo when present
    const expectedMemo = requirements.extra?.memo as string | undefined;
    if (expectedMemo) {
      const memoInstructions = optionalInstructions.filter(
        ix => ix.programAddress.toString() === MEMO_PROGRAM_ADDRESS,
      );
      if (memoInstructions.length !== 1) {
        return {
          isValid: false,
          invalidReason: "invalid_exact_svm_payload_memo_count",
          payer,
        };
      }
      const memoData = memoInstructions[0].data;
      const actualMemo = memoData ? new TextDecoder().decode(new Uint8Array(memoData)) : "";
      if (actualMemo !== expectedMemo) {
        return {
          isValid: false,
          invalidReason: "invalid_exact_svm_payload_memo_mismatch",
          payer,
        };
      }
    }

    // Step 6: Sign and Simulate Transaction
    // CRITICAL: Simulation proves transaction will succeed (catches insufficient balance, invalid accounts, etc)
    try {
      const feePayer = requirements.extra.feePayer as Address;

      // Sign transaction with the feePayer's signer
      const fullySignedTransaction = await this.signer.signTransaction(
        exactSvmPayload.transaction,
        feePayer,
        requirements.network,
      );

      // Simulate to verify transaction would succeed
      await this.signer.simulateTransaction(fullySignedTransaction, requirements.network);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return {
        isValid: false,
        invalidReason: "transaction_simulation_failed",
        invalidMessage: errorMessage,
        payer,
      };
    }

    return {
      isValid: true,
      invalidReason: undefined,
      payer,
    };
  }

  /**
   * Settles a payment by submitting the transaction.
   * Ensures the correct signer is used based on the feePayer specified in requirements.
   *
   * @param payload - The payment payload to settle
   * @param requirements - The payment requirements
   * @returns Promise resolving to settlement response
   */
  async settle(
    payload: PaymentPayload,
    requirements: PaymentRequirements,
  ): Promise<SettleResponse> {
    const exactSvmPayload = payload.payload as ExactSvmPayloadV2;

    const valid = await this.verify(payload, requirements);
    if (!valid.isValid) {
      return {
        success: false,
        network: payload.accepted.network,
        transaction: "",
        errorReason: valid.invalidReason ?? "verification_failed",
        errorMessage: valid.invalidMessage,
        payer: valid.payer || "",
      };
    }

    // Decode the transaction to compute the message hash used as the cache key.
    // Must remain synchronous (before any await) so concurrent settle calls for
    // the same payment are caught before any async work begins.
    const decodedTx = decodeTransactionFromPayload(exactSvmPayload);

    // Duplicate settlement check keyed on message hash (immune to mutable fee-payer sig at slot 0).
    const txKey = transactionMessageHash(decodedTx);
    if (this.settlementCache.isDuplicate(txKey)) {
      return {
        success: false,
        network: payload.accepted.network,
        transaction: "",
        errorReason: "duplicate_settlement",
        payer: valid.payer || "",
      };
    }

    try {
      // Extract feePayer from requirements (already validated in verify)
      const feePayer = requirements.extra.feePayer as Address;

      // Sign transaction with the feePayer's signer
      const fullySignedTransaction = await this.signer.signTransaction(
        exactSvmPayload.transaction,
        feePayer,
        requirements.network,
      );

      // Send transaction to network
      const signature = await this.signer.sendTransaction(
        fullySignedTransaction,
        requirements.network,
      );

      // Wait for confirmation
      await this.signer.confirmTransaction(signature, requirements.network);

      return {
        success: true,
        transaction: signature,
        network: payload.accepted.network,
        payer: valid.payer,
      };
    } catch (error) {
      console.error("Failed to settle transaction:", error);
      return {
        success: false,
        errorReason: "transaction_failed",
        errorMessage: error instanceof Error ? error.message : String(error),
        transaction: "",
        network: payload.accepted.network,
        payer: valid.payer || "",
      };
    }
  }

  /**
   * Verify that the compute limit instruction is valid and within bounds.
   *
   * Security: Enforces MAX_COMPUTE_UNIT_LIMIT to prevent attackers from
   * inflating CU budget at the facilitator's expense (gas amplification attack).
   *
   * @param instruction - The compute limit instruction
   * @param instruction.programAddress - Program address
   * @param instruction.data - Instruction data bytes
   */
  private verifyComputeLimitInstruction(instruction: {
    programAddress: Address;
    data?: Readonly<Uint8Array>;
  }): void {
    const programAddress = instruction.programAddress.toString();

    if (
      programAddress !== COMPUTE_BUDGET_PROGRAM_ADDRESS.toString() ||
      !instruction.data ||
      instruction.data[0] !== 2 // discriminator for SetComputeUnitLimit
    ) {
      throw new Error(
        "invalid_exact_svm_payload_transaction_instructions_compute_limit_instruction",
      );
    }

    try {
      const parsedInstruction = parseSetComputeUnitLimitInstruction(instruction as never);

      // Security: Check compute unit limit doesn't exceed maximum
      // Prevents gas amplification attacks where attacker inflates CU at facilitator's cost
      // Note: parsed instruction has structure { programAddress, data: { discriminator, units } }
      const units = parsedInstruction.data.units;
      if (units > MAX_COMPUTE_UNIT_LIMIT) {
        throw new Error("invalid_exact_svm_payload_transaction_compute_limit_too_high");
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("too_high")) {
        throw error;
      }
      throw new Error(
        "invalid_exact_svm_payload_transaction_instructions_compute_limit_instruction",
      );
    }
  }

  /**
   * Verify that the compute price instruction is valid.
   *
   * @param instruction - The compute price instruction
   * @param instruction.programAddress - Program address
   * @param instruction.data - Instruction data bytes
   */
  private verifyComputePriceInstruction(instruction: {
    programAddress: Address;
    data?: Readonly<Uint8Array>;
  }): void {
    const programAddress = instruction.programAddress.toString();

    if (
      programAddress !== COMPUTE_BUDGET_PROGRAM_ADDRESS.toString() ||
      !instruction.data ||
      instruction.data[0] !== 3 // discriminator for SetComputeUnitPrice
    ) {
      throw new Error(
        "invalid_exact_svm_payload_transaction_instructions_compute_price_instruction",
      );
    }

    try {
      const parsedInstruction = parseSetComputeUnitPriceInstruction(instruction as never);

      // Check if price exceeds maximum (5 lamports per compute unit)
      // Note: parsed instruction has structure { programAddress, data: { discriminator, microLamports } }
      const microLamports = parsedInstruction.data.microLamports;
      if (microLamports > BigInt(MAX_COMPUTE_UNIT_PRICE_MICROLAMPORTS)) {
        throw new Error(
          "invalid_exact_svm_payload_transaction_instructions_compute_price_instruction_too_high",
        );
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("too_high")) {
        throw error;
      }
      throw new Error(
        "invalid_exact_svm_payload_transaction_instructions_compute_price_instruction",
      );
    }
  }
}
